import {FC, useState} from 'react';
import {useNavigate} from 'react-router-dom';
//mui
import {Box, IconButton, Typography, useMediaQuery} from '@mui/material';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
  getOrganizationIsLoadingSelector,
  getOrganizationSelector,
  getTeamSelector,
} from '@/store/organization/selector';
import {organizationLogout} from '@/store/organization/actions';
import {clearEventData} from '@/store/organization-event';
//types
import {IOrganization, ITeam} from '@/utils/types';
//images
import organizationIcon from '@images/organization-example.png';
//icons
import {ReactComponent as Arrow} from '@icons/arrow-down.svg';
//enums
import {ROUTER_URL, STORAGE_KEY} from '@/utils/enums';
//components
import Dropdown from '@/shared/components/Dropdown';
//constants
import {BODY1, IMAGE_AVATAR_ALT, SUBTITLE2} from '@/utils/constants';
import {orgStorage} from '@/utils/services';
//styles
import {organizationDropdownStyle} from './OrganizationDropdown.styles';
import Loader from '@/shared/components/Loader';
import {setNotifications} from '@/store/notification';

const OrganizationDropdown: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isOpened, setIsOpened] = useState(false);

  const organization: IOrganization | null = useAppSelector(getOrganizationSelector);

  const team: ITeam | null = useAppSelector(getTeamSelector);

  const isLoading: boolean = useAppSelector(getOrganizationIsLoadingSelector);

  const organizationRole = orgStorage.get(STORAGE_KEY.ORGANIZATION_ROLE);

  const handleLogout = async () => {
    dispatch(organizationLogout());
    dispatch(clearEventData());
    dispatch(setNotifications({notifications: [], target: 'unread'}));
    dispatch(setNotifications({notifications: [], target: 'all'}));

    navigate(`/${ROUTER_URL.LOGIN}`);
  };

  const handleProfileClick = () => {
    orgStorage.set('activeTab', 'Settings');
    navigate(ROUTER_URL.ORGANIZATION_PROFILE);
  };
  const handleNotificationClick = () => {
    orgStorage.set('activeTab', 'Notifications');
    navigate(ROUTER_URL.ORGANIZATION_PROFILE_NOTIFICATION);
  };
  const handleTeamClick = () => {
    orgStorage.set('activeTab', 'Team');
    navigate(ROUTER_URL.ORGANIZATION_PROFILE_TEAM);
  };
  const handleContactListClick = () => {
    orgStorage.set('activeTab', 'Contacts');
    navigate(ROUTER_URL.ORGANIZATION_PROFILE_CONTACT_LIST);
  };
  const handleIntegrationClick = () => {
    orgStorage.set('activeTab', 'Integrations');
    navigate(ROUTER_URL.ORGANIZATION_PROFILE_INTEGRATION);
  };
  const handleBillingClick = () => {
    orgStorage.set('activeTab', 'Billing');
    navigate(ROUTER_URL.ORGANIZATION_PROFILE_BILLING);
  };

  const isMobile = useMediaQuery('(max-width:640px)');

  return (
    <Box>
      {isLoading ? (
        <Loader />
      ) : (
        <Dropdown
          toggleContent={
            <Box sx={organizationDropdownStyle.wrap}>
              <IconButton sx={organizationDropdownStyle.avatar}>
                <img
                  src={
                    organizationRole === 'Owner' && !!organization?.logo
                      ? organization.logo
                      : team && !!team?.logo
                      ? team.logo
                      : organizationIcon
                  }
                  alt={IMAGE_AVATAR_ALT}
                />
              </IconButton>
              <Box sx={organizationDropdownStyle.info}>
                <Typography variant={BODY1} sx={organizationDropdownStyle.name}>
                  {organization ? organization.name : `${team?.firstName} ${team?.lastName}`}
                </Typography>
                <Typography variant={SUBTITLE2} sx={organizationDropdownStyle.email}>
                  {organization ? organization.email : team?.email}
                </Typography>
              </Box>
              {!isMobile && (
                <IconButton
                  disableRipple
                  sx={{
                    ...organizationDropdownStyle.dropdownIcon,
                    ...(isOpened && organizationDropdownStyle.rotateArrow),
                  }}
                >
                  <Arrow />
                </IconButton>
              )}
            </Box>
          }
          options={
            <Box sx={organizationDropdownStyle.options}>
              <Typography onClick={handleProfileClick} sx={organizationDropdownStyle.option} variant={BODY1}>
                Settings
              </Typography>
              <Typography onClick={handleNotificationClick} sx={organizationDropdownStyle.option} variant={BODY1}>
                Notifications
              </Typography>
              {(organizationRole === 'Admin' || organizationRole === 'Owner') && (
                <Typography onClick={handleBillingClick} sx={organizationDropdownStyle.option} variant={BODY1}>
                  Billing
                </Typography>
              )}
              {(organizationRole === 'Admin' || organizationRole === 'Owner') && (
                <Typography onClick={handleTeamClick} sx={organizationDropdownStyle.option} variant={BODY1}>
                  Team
                </Typography>
              )}
              <Typography onClick={handleContactListClick} sx={organizationDropdownStyle.option} variant={BODY1}>
                Contacts
              </Typography>
              {/* {(organizationRole === 'Admin' || organizationRole === 'Owner') && (
                <Typography onClick={handleIntegrationClick} sx={organizationDropdownStyle.option} variant={BODY1}>
                  Integrations
                </Typography>
              )} */}
              <Typography sx={organizationDropdownStyle.option} variant={BODY1} onClick={handleLogout}>
                Log out
              </Typography>
            </Box>
          }
          open={isOpened}
          onOpen={() => {
            if (isMobile) {
              navigate(ROUTER_URL.ORGANIZATION_ACCOUNT_SETTINGS);
              return;
            }
            setIsOpened(true);
          }}
          onClose={() => setIsOpened(false)}
        />
      )}
    </Box>
  );
};

export default OrganizationDropdown;
